import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { BackgroundJob, WorkerRun, WorkerRunStatus } from '@neuroedge/shared';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  useGridState,
  type GridColumn,
} from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useBackgroundJobs, useSetWorkerSchedule, useWorkerRuns } from '../../api/background-jobs';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

const HEALTH: Record<BackgroundJob['health'], { label: string; color?: string }> = {
  HEALTHY: { label: 'Healthy', color: 'var(--color-success)' },
  WARNING: { label: 'Last run partly failed', color: '#d97706' },
  FAILING: { label: 'Last run failed', color: 'var(--color-danger)' },
  LATE: { label: 'Not running on schedule', color: 'var(--color-danger)' },
  RUNNING: { label: 'Running now' },
  NEVER_RUN: { label: 'No runs yet', color: 'var(--color-text-muted)' },
};

const STATUS: Record<WorkerRunStatus, { label: string; color?: string }> = {
  RUNNING: { label: 'Running' },
  SUCCEEDED: { label: 'Succeeded', color: 'var(--color-success)' },
  PARTIAL: { label: 'Partly failed', color: '#d97706' },
  FAILED: { label: 'Failed', color: 'var(--color-danger)' },
};

const when = (iso: string) => new Date(iso).toLocaleString();
const duration = (ms: number | null) =>
  ms === null
    ? '—'
    : ms < 1000
      ? `${ms} ms`
      : ms < 60_000
        ? `${(ms / 1000).toFixed(1)} s`
        : `${Math.round(ms / 60_000)} min`;

function relative(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const mins = Math.round(Math.abs(diff) / 60_000);
  const text =
    mins < 1 ? 'less than a minute' : mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`;
  return diff >= 0 ? `in ${text}` : `${text} ago`;
}

/** "Every 5 minutes", "Every hour", "Daily at 08:00 (your time)" - else the raw schedule. */
export function describeCron(cron: string | null): string {
  if (!cron) return 'Not set';
  const [m, h, dom, mon, dow] = cron.trim().split(/\s+/);
  const everyDay = dom === '*' && mon === '*' && dow === '*';
  if (!everyDay) return `${cron} (UTC)`;
  if (m === '*' && h === '*') return 'Every minute';
  const step = /^\*\/(\d+)$/.exec(m ?? '');
  if (step && h === '*') return `Every ${step[1]} minutes`;
  if (/^\d+$/.test(m ?? '') && h === '*') return `Every hour at :${m!.padStart(2, '0')}`;
  if (/^\d+$/.test(m ?? '') && /^\d+$/.test(h ?? '')) {
    const utc = new Date(Date.UTC(2026, 0, 1, Number(h), Number(m)));
    return `Daily at ${utc.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (your time)`;
  }
  return `${cron} (UTC)`;
}

/** The job's own result counts as "key: value" lines (one level of nesting). */
function SummaryList({ summary }: { summary: Record<string, unknown> }) {
  const entries: [string, string][] = [];
  for (const [k, v] of Object.entries(summary)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
        entries.push([`${k} · ${k2}`, String(v2)]);
      }
    } else {
      entries.push([k, v === null ? '—' : String(v)]);
    }
  }
  return (
    <dl
      style={{
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '2px var(--space-3)',
      }}
    >
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt style={muted}>{k}</dt>
          <dd style={{ margin: 0 }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ScheduleModal({ job, onClose }: { job: BackgroundJob; onClose: () => void }) {
  const save = useSetWorkerSchedule();
  const toast = useToast();
  const [cron, setCron] = useState(job.cron ?? '');
  const [error, setError] = useState<string | undefined>();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (cron.trim().split(/\s+/).length !== 5) {
      setError('Enter 5 parts: minute hour day-of-month month day-of-week, e.g. */5 * * * *.');
      return;
    }
    try {
      await save.mutateAsync({ worker: job.worker, cron: cron.trim() });
      toast.success('Schedule saved.');
      onClose();
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setError(fields.cron ?? (err instanceof Error ? err.message : 'Could not save.'));
    }
  }

  return (
    <Modal title={`Schedule - ${job.label}`} onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-3)' }}
      >
        <p style={{ margin: 0, ...muted }}>
          Enter the same Cron Schedule as on this job&apos;s Railway service (Railway uses UTC). It
          is only used here to show the next run and to warn when a run is missed - it doesn&apos;t
          change Railway.
        </p>
        <TextField
          label="Cron schedule (UTC)"
          value={cron}
          onChange={(e) => {
            setCron(e.target.value);
            setError(undefined);
          }}
          error={error}
        />
        <span style={muted}>{describeCron(cron)}</span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function JobCard({ job, onEdit }: { job: BackgroundJob; onEdit: () => void }) {
  const h = HEALTH[job.health];
  const last = job.lastRun;
  return (
    <Card>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <strong>{job.label}</strong>
          <span className="ne-badge" style={{ color: h.color }}>
            {h.label}
          </span>
        </div>
        <span style={muted}>{job.description}</span>
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px var(--space-3)',
          }}
        >
          <dt style={muted}>Last run</dt>
          <dd style={{ margin: 0 }}>
            {last ? (
              <>
                {relative(last.startedAt)} ·{' '}
                <span style={{ color: STATUS[last.status].color }}>
                  {STATUS[last.status].label}
                </span>
                {last.durationMs !== null ? ` · ${duration(last.durationMs)}` : ''}
              </>
            ) : (
              'Never'
            )}
          </dd>
          <dt style={muted}>Next run</dt>
          <dd style={{ margin: 0 }}>
            {job.nextRunAt ? `${relative(job.nextRunAt)} (${when(job.nextRunAt)})` : '—'}
          </dd>
          <dt style={muted}>Schedule</dt>
          <dd style={{ margin: 0 }}>
            {describeCron(job.cron)}{' '}
            <button
              type="button"
              onClick={onEdit}
              style={{ all: 'unset', cursor: 'pointer', color: 'var(--color-primary)' }}
              aria-label={`Edit schedule for ${job.label}`}
            >
              Edit
            </button>
          </dd>
          <dt style={muted}>Last 24 h</dt>
          <dd style={{ margin: 0 }}>
            {job.last24h.runs} runs
            {job.last24h.failed ? (
              <span style={{ color: 'var(--color-danger)' }}> · {job.last24h.failed} failed</span>
            ) : null}
            {job.last24h.partial ? ` · ${job.last24h.partial} partly failed` : ''}
          </dd>
        </dl>
        {job.missedSince ? (
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            The run due {relative(job.missedSince)} didn&apos;t happen. Check the job&apos;s Railway
            service (Cron Runs and logs) - see docs/RAILWAY_CRON_SETUP.md.
          </p>
        ) : null}
        {last?.error ? (
          <p
            style={{
              margin: 0,
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              overflowWrap: 'anywhere',
            }}
          >
            {last.error}
          </p>
        ) : null}
        {job.lastDays.length > 0 ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {job.lastDays.map((d) => (
              <span
                key={d.day}
                className="ne-badge"
                title={`${d.runs} runs, ${d.failed} failed, ${d.partial} partly failed`}
                style={{
                  color: d.failed ? 'var(--color-danger)' : d.partial ? '#d97706' : undefined,
                }}
              >
                {new Date(`${d.day}T00:00:00`).toLocaleDateString([], {
                  day: 'numeric',
                  month: 'short',
                })}
                : {d.runs}
                {d.failed ? ` (${d.failed} ✕)` : ''}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

const STATUS_TABS: { value: WorkerRunStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PARTIAL', label: 'Partly failed' },
  { value: 'SUCCEEDED', label: 'Succeeded' },
  { value: 'RUNNING', label: 'Running' },
];

function RunHistory({ jobs }: { jobs: BackgroundJob[] }) {
  const [tab, setTab] = useState<WorkerRunStatus | 'ALL'>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'startedAt', sortOrder: 'desc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const f = grid.filters;
  const runs = useWorkerRuns({
    worker: f.worker || undefined,
    status: tab === 'ALL' ? undefined : tab,
    startedFrom: dayBoundary(f.startedAtFrom, false),
    startedTo: dayBoundary(f.startedAtTo, true),
    page: grid.page,
    pageSize: grid.pageSize,
  });
  const labelOf = new Map(jobs.map((j) => [j.worker, j.label]));
  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: GridColumn<WorkerRun>[] = [
    {
      key: 'startedAt',
      header: 'Started',
      filter: { type: 'dateRange' },
      render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{when(r.startedAt)}</span>,
    },
    {
      key: 'worker',
      header: 'Job',
      filter: {
        type: 'select',
        options: jobs.map((j) => ({ value: j.worker, label: j.label })),
      },
      render: (r) => labelOf.get(r.worker) ?? r.worker,
    },
    {
      key: 'status',
      header: 'Result',
      render: (r) => (
        <span className="ne-badge" style={{ color: STATUS[r.status].color }}>
          {STATUS[r.status].label}
        </span>
      ),
    },
    { key: 'durationMs', header: 'Took', align: 'right', render: (r) => duration(r.durationMs) },
    {
      key: 'details',
      header: '',
      render: (r) =>
        r.summary || r.error ? (
          <Button type="button" variant="secondary" onClick={() => toggle(r.id)}>
            {expanded.has(r.id) ? 'Hide' : 'Details'}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card title="Run history">
      <div role="tablist" aria-label="Run result" className="ne-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className="ne-tabs__tab"
            onClick={() => {
              setTab(t.value);
              setGrid({ ...grid, page: 1 });
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        {runs.isError ? (
          <PageState
            kind="error"
            message={runs.error.message}
            onRetry={() => void runs.refetch()}
          />
        ) : (
          <DataGrid
            columns={columns}
            rows={runs.data?.items ?? []}
            rowKey={(r) => r.id}
            rowLabel={(r) => `${r.worker} ${r.startedAt}`}
            total={runs.data?.meta.total ?? 0}
            state={grid}
            onStateChange={setGrid}
            loading={runs.isFetching}
            emptyMessage="No runs recorded yet."
            renderExpanded={(r) =>
              expanded.has(r.id) ? (
                <div style={{ display: 'grid', gap: 'var(--space-2)', padding: 'var(--space-2)' }}>
                  {r.error ? (
                    <p
                      style={{ margin: 0, color: 'var(--color-danger)', overflowWrap: 'anywhere' }}
                    >
                      {r.error}
                    </p>
                  ) : null}
                  {r.summary ? <SummaryList summary={r.summary} /> : null}
                </div>
              ) : null
            }
          />
        )}
      </div>
    </Card>
  );
}

/**
 * Admin → Background jobs: the cron workers' health, last and next run,
 * schedule, daily counts and full run history (kept 60 days).
 */
export function BackgroundJobsPage() {
  const { isPlatformAdmin } = useAuth();
  const jobs = useBackgroundJobs();
  const [editing, setEditing] = useState<BackgroundJob | null>(null);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1100 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Background jobs</h1>
        <p style={{ margin: 'var(--space-1) 0 0', ...muted }}>
          Scheduled jobs that run on Railway Cron. Every run is recorded here; a job is flagged when
          a scheduled run doesn&apos;t happen (a run that can&apos;t even start only shows in
          Railway&apos;s own logs).
        </p>
      </div>
      {jobs.isPending ? (
        <PageState kind="loading" />
      ) : jobs.isError ? (
        <PageState kind="error" message={jobs.error.message} onRetry={() => void jobs.refetch()} />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-3)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            }}
          >
            {jobs.data.map((job) => (
              <JobCard key={job.worker} job={job} onEdit={() => setEditing(job)} />
            ))}
          </div>
          <RunHistory jobs={jobs.data} />
        </>
      )}
      {editing ? <ScheduleModal job={editing} onClose={() => setEditing(null)} /> : null}
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/dashboard">Admin dashboard</Link>
      </p>
    </div>
  );
}
